import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Clock, Trash2, Phone, Mail } from "lucide-react";
import api, { track } from "@/lib/api";

export default function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    toast.success(status === "treated" ? "Marqué comme traité" : "Remis en attente");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    await api.delete(`/admin/leads/${id}`);
    load();
  };

  const shown = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div>
      <h1 className="font-display text-3xl font-black tracking-tight mb-1">Demandes de contact</h1>
      <p className="text-zinc-500 text-sm mb-6">Gare de triage des leads — {leads.filter((l) => l.status === "new").length} en attente</p>

      <div className="flex gap-2 mb-6">
        {[{ k: "all", l: "Toutes" }, { k: "new", l: "En attente" }, { k: "treated", l: "Traitées" }].map((f) => (
          <button key={f.k} data-testid={`leads-filter-${f.k}`} onClick={() => setFilter(f.k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === f.k ? "bg-yellow-400 text-black" : "bg-white/5 text-zinc-400 border border-white/10"}`}>
            {f.l}
          </button>
        ))}
      </div>

      <div className="bg-[#18181B] border border-white/5 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3">Contact</th>
              <th className="text-left px-5 py-3">Projet / Message</th>
              <th className="text-left px-5 py-3">Dispo</th>
              <th className="text-left px-5 py-3">Statut</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((l) => (
              <tr key={l.id} data-testid="lead-row" className="border-t border-white/5 hover:bg-zinc-800/40">
                <td className="px-5 py-4">
                  <div className="font-medium">{l.civility} {l.firstname} {l.name}</div>
                  <div className="flex flex-col gap-0.5 mt-1 text-xs text-zinc-400">
                    {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-yellow-400"><Phone size={12} />{l.phone}</a>}
                    {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-yellow-400"><Mail size={12} />{l.email}</a>}
                  </div>
                </td>
                <td className="px-5 py-4 max-w-xs text-zinc-300">
                  {l.project_type && <span className="inline-block bg-yellow-400/15 text-yellow-400 text-[11px] font-semibold px-2 py-0.5 rounded-full mb-1">{l.project_type}</span>}
                  {l.deadline && <div className="text-[11px] text-zinc-400">📅 Souhaité : {l.deadline}</div>}
                  {l.how_heard && <div className="text-[11px] text-zinc-500">Via : {l.how_heard}</div>}
                  {l.message && <div className="text-sm mt-1">{l.message}</div>}
                  {!l.project_type && !l.deadline && !l.how_heard && !l.message && <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-5 py-4 text-zinc-400">{l.availability}</td>
                <td className="px-5 py-4">
                  {l.status === "treated" ? (
                    <span className="inline-flex items-center gap-1 text-xs bg-green-400/15 text-green-400 px-2.5 py-1 rounded-full"><Check size={12} /> Traité</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs bg-yellow-400/15 text-yellow-400 px-2.5 py-1 rounded-full"><Clock size={12} /> En attente</span>
                  )}
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-2">
                    {l.status === "treated" ? (
                      <button data-testid="lead-untreat" onClick={() => setStatus(l.id, "new")} className="text-xs px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10">En attente</button>
                    ) : (
                      <button data-testid="lead-treat" onClick={() => setStatus(l.id, "treated")} className="text-xs px-3 py-1.5 rounded-md bg-green-400/15 text-green-400 hover:bg-green-400/25">Traiter</button>
                    )}
                    <button data-testid="lead-delete" onClick={() => remove(l.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && <p className="text-center text-zinc-500 py-12">Aucune demande.</p>}
      </div>
    </div>
  );
}
